import axios from 'axios';
import { BACKEND_KEYS } from '../../common/consts/app-keys.const';

export interface IUserWebhook {
  id: number;
  name: string;
  provider: 'discord' | 'slack';
  url: string;
  channelUrl?: string | null;
  createdAt: string;
}
import {
  HttpFactoryService,
  HttpService,
  getStoredToken,
  setStoredToken,
  clearStoredToken
} from '../../common/services';
import {
  IActivateAccount,
  ISignin,
  IForgotPassword,
  ICheckRecoveryCode,
  IResetPassword
} from '../types';

class AuthService {
  httpService: HttpService;

  constructor(private httpFactory: HttpFactoryService) {
    this.httpService = httpFactory.createHttpService();
  }

  public async login(payload: ISignin) {
    const result = await this.httpService.post<
      { access_token?: string; user?: { email: string }; mfaRequired?: boolean; mfaToken?: string },
      ISignin
    >(BACKEND_KEYS.SIGNIN, payload);
    if ((result as any)?.mfaRequired) {
      return result as { mfaRequired: true; mfaToken: string; mfaMethods: { totp: boolean; email: boolean } };
    }
    if (!result?.user?.email) throw new Error('Error while signing in');
    if (result.access_token) setStoredToken(result.access_token);
    return result as { access_token: string; user: { email: string } };
  }

  public async signup(payload: ISignin) {
    const result = await this.httpService.post<
      {
        autoActivated: boolean;
        access_token?: string;
        user?: { email: string };
      },
      ISignin
    >(BACKEND_KEYS.SIGNUP, payload);
    if (!result) throw new Error('Error while signing up');
    if (result.access_token) setStoredToken(result.access_token);
    return result;
  }

  public async activateAccount(payload: IActivateAccount) {
    const result = await this.httpService.post<
      { access_token: string; user: { email: string } },
      { token: string }
    >(BACKEND_KEYS.ACTIVATE_ACCOUNT, { token: payload.code });
    if (!result?.user?.email) throw new Error('Error while activating account');
    if (result.access_token) setStoredToken(result.access_token);
    return result;
  }

  public async forgotPassword(payload: IForgotPassword) {
    const result: true | void = await this.httpService.post(
      BACKEND_KEYS.FORGOT_PASSWORD,
      payload
    );
    if (result !== true) throw new Error('Error while sending recovery code');
    return payload.email;
  }

  public async checkRecoveryCode(payload: ICheckRecoveryCode) {
    const result: true | void = await this.httpService.post(
      BACKEND_KEYS.CHECK_RECOVERY_CODE,
      payload
    );
    if (result !== true) throw new Error('Error while checking recovery code');
    return payload.token;
  }

  public async resetPassword(payload: IResetPassword) {
    const result = await this.httpService.post(BACKEND_KEYS.PASSWORD_RESET, payload);
    if (!result) throw new Error('Error while resetting password');
    return result;
  }

  public async resendRecoveryCode(payload: IForgotPassword) {
    await this.httpService.post(BACKEND_KEYS.RESEND_RECOVERY_CODE, payload);
    return payload.email;
  }

  public async resendActivationCode(payload: IForgotPassword) {
    await this.httpService.post(BACKEND_KEYS.RESEND_ACTIVATION_CODE, payload);
    return payload.email;
  }

  public async getMe(): Promise<{
    user: {
      id: number;
      email: string;
      isAdmin: boolean;
      displayName: string | null;
      avatar: string | null;
      hasActiveSubscription: boolean;
      subscriptionTier: string | null;
      subscriptionExpiresAt: string | null;
      subscriptionCancelledAt: string | null;
      isProGifted: boolean;
      hasFreeTrialAbility: boolean;
      isPro: boolean;
      mfaEnabled: boolean;
      mfaEmailEnabled: boolean;
      mfaPushEnabled: boolean;
    };
  } | null> {
    try {
      const baseUrl =
        process.env.NODE_ENV === 'development'
          ? `${process.env.REACT_APP_BACKEND_URL_DEV}/api/v1`
          : `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
      const token = getStoredToken();
      const result = await axios.get(`${baseUrl}/${BACKEND_KEYS.ME}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      return result.data ?? null;
    } catch {
      return null;
    }
  }

  public async logout(): Promise<void> {
    await this.httpService.post(BACKEND_KEYS.LOGOUT, {});
    clearStoredToken();
  }

  public async getUserWebhooks(): Promise<IUserWebhook[]> {
    const res = await this.httpService.get<IUserWebhook[]>(BACKEND_KEYS.USER_WEBHOOKS);
    return (res as unknown as IUserWebhook[]) || [];
  }

  public async createUserWebhook(data: { name: string; url: string; provider: string; channelUrl?: string | null }): Promise<IUserWebhook> {
    const res = await this.httpService.post<IUserWebhook, typeof data>(BACKEND_KEYS.USER_WEBHOOKS, data);
    return res as unknown as IUserWebhook;
  }

  public async deleteUserWebhook(id: number): Promise<void> {
    await this.httpService.delete(`${BACKEND_KEYS.USER_WEBHOOKS}/${id}`);
  }

  public async updateUserWebhook(id: number, data: { name?: string; url?: string; channelUrl?: string | null }): Promise<IUserWebhook> {
    const res = await this.httpService.patch<IUserWebhook, typeof data>(`${BACKEND_KEYS.USER_WEBHOOKS}/${id}`, data);
    return res as unknown as IUserWebhook;
  }

  // MFA
  public async mfaSetup(): Promise<{ secret: string; qrCode: string; otpauthUrl: string }> {
    return this.httpService.post(BACKEND_KEYS.MFA_SETUP, {}) as any;
  }

  public async mfaEnable(code: string): Promise<{ backupCodes: string[] }> {
    return this.httpService.post(BACKEND_KEYS.MFA_ENABLE, { code }) as any;
  }

  public async mfaDisable(code: string): Promise<{ success: boolean }> {
    return this.httpService.post(BACKEND_KEYS.MFA_DISABLE, { code }) as any;
  }

  public async mfaVerify(mfaToken: string, code: string, remember?: boolean): Promise<{ access_token: string; user: { email: string } }> {
    const result = await this.httpService.post<
      { access_token: string; user: { email: string } },
      { mfaToken: string; code: string; remember?: boolean }
    >(BACKEND_KEYS.MFA_VERIFY, { mfaToken, code, remember });
    if (result?.access_token) setStoredToken(result.access_token);
    return result as any;
  }

  public async mfaStatus(): Promise<{ mfaEnabled: boolean; mfaEmailEnabled: boolean; mfaPushEnabled: boolean }> {
    return this.httpService.get(BACKEND_KEYS.MFA_STATUS) as any;
  }

  // Email OTP
  public async mfaEmailSetup(): Promise<{ sent: boolean }> {
    return this.httpService.post(BACKEND_KEYS.MFA_EMAIL_SETUP, {}) as any;
  }

  public async mfaEmailEnable(code: string): Promise<{ success: boolean }> {
    return this.httpService.post(BACKEND_KEYS.MFA_EMAIL_ENABLE, { code }) as any;
  }

  public async mfaEmailDisable(): Promise<{ success: boolean }> {
    return this.httpService.post(BACKEND_KEYS.MFA_EMAIL_DISABLE, {}) as any;
  }

  public async mfaEmailSend(mfaToken: string): Promise<{ sent: boolean }> {
    return this.httpService.post(BACKEND_KEYS.MFA_EMAIL_SEND, { mfaToken }) as any;
  }

  public async logoutAll(): Promise<{ success: boolean }> {
    return this.httpService.post(BACKEND_KEYS.LOGOUT_ALL, {}) as any;
  }

  // Push MFA
  public async mfaPushEnable(): Promise<{ success: boolean }> {
    return this.httpService.post(BACKEND_KEYS.MFA_PUSH_ENABLE, {}) as any;
  }

  public async mfaPushDisable(): Promise<{ success: boolean }> {
    return this.httpService.post(BACKEND_KEYS.MFA_PUSH_DISABLE, {}) as any;
  }

  public async mfaPushSend(mfaToken: string, excludeEndpoint?: string): Promise<{ challengeId: string }> {
    return this.httpService.post(BACKEND_KEYS.MFA_PUSH_SEND, { mfaToken, excludeEndpoint }) as any;
  }

  public async mfaPushRespond(challengeId: string, action: 'approve' | 'deny'): Promise<{ success: boolean }> {
    return this.httpService.post(BACKEND_KEYS.MFA_PUSH_RESPOND, { challengeId, action }) as any;
  }

  public async mfaPushStatus(challengeId: string): Promise<{ status: string }> {
    return this.httpService.get(`${BACKEND_KEYS.MFA_PUSH_STATUS}/${challengeId}`) as any;
  }

  public async mfaPushCheckSubscriptions(): Promise<{ hasSubscriptions: boolean }> {
    return this.httpService.post(BACKEND_KEYS.MFA_PUSH_CHECK_SUBS, {}) as any;
  }
}

const authService = new AuthService(new HttpFactoryService());

export { authService };
